// ❌ REMOVIDO: import { prisma } from './prisma';
import { TENANT_DEFAULT_COLORS as D } from './tenant-defaults';

// Cache em memória para desenvolvimento
const tenantCache = new Map<string, any>();

// ❌ REMOVIDO: Funções que dependem do Prisma (executam no servidor)
// export async function detectTenant(host: string, subdomain?: string, slug?: string): Promise<any> { ... }
// export async function setTenantId(tenantId: string): Promise<void> { ... }
// export async function getCurrentTenantId(): Promise<string | null> { ... }
// export async function clearTenantId(): Promise<void> { ... }

/**
 * Cria um contexto de tenant para uso em queries
 */
export function createTenantContext(tenantId: string) {
  return {
    tenant_id: tenantId
  };
}

/**
 * Gera variáveis CSS customizadas baseadas no tenant
 * ✅ FUNÇÃO QUE PODE EXECUTAR NO CLIENTE
 */
export function generateTenantCSS(tenant: any): string {
  // ✅ Verificar se tenant tem configurações
  if (!tenant || !tenant.configurations) {
    console.warn('Tenant sem configurações, usando CSS padrão');
    return '';
  }

  const config = tenant.configurations;
  
  // CSS Variables — emite todos os aliases consumidos pelo globals.css da tenant.
  // Bugs 1, 2 e 3 corrigidos: aliases PT+EN, highlight+accent, defaults unificados.
  const cssVars = [
    // Base
    `--tenant-primary-color: ${config.cor_primaria || D.primary};`,
    `--tenant-secondary-color: ${config.cor_secundaria || D.secondary};`,
    // Bug 2 corrigido: emite highlight E accent (globals.css consome accent)
    `--tenant-highlight-color: ${config.cor_destaque || D.accent};`,
    `--tenant-accent-color: ${config.cor_destaque || D.accent};`,
    `--tenant-accent: ${config.cor_destaque || D.accent};`,
    `--tenant-background-color: ${config.cor_fundo || D.background};`,
    `--tenant-text-color: ${config.cor_texto || D.text};`,
    // Aliases curtos (consumidos pelo globals.css)
    `--tenant-primary: ${config.cor_primaria || D.primary};`,
    `--tenant-secondary: ${config.cor_secundaria || D.secondary};`,
    `--tenant-background: ${config.cor_fundo || D.background};`,
    `--tenant-text: ${config.cor_texto || D.text};`,
    // Compatibilidade com código legado
    `--primary-color: ${config.cor_primaria || D.primary};`,
    `--secondary-color: ${config.cor_secundaria || D.secondary};`,
    `--highlight-color: ${config.cor_destaque || D.accent};`,
    `--background-color: ${config.cor_fundo || D.background};`,
    `--text-color: ${config.cor_texto || D.text};`,
    // Botões e UI
    `--tenant-button-bg: ${config.cor_destaque || D.accent};`,
    `--tenant-button-hover: ${config.cor_primaria || D.primary};`,
    `--tenant-link-color: ${config.cor_primaria || D.primary};`,
    `--tenant-border-color: ${config.cor_secundaria || D.secondary};`,
    `--tenant-shadow-color: ${config.cor_primaria || D.primary}20;`,
    // Bug 1 corrigido: emite grafia PT (legado) E grafia EN (consumida pelo globals.css)
    `--tenant-nome-primary: ${config.cor_nome_primaria || config.cor_primaria || D.primary};`,
    `--tenant-name-primary-color: ${config.cor_nome_primaria || config.cor_primaria || D.primary};`,
    `--tenant-name-primary: ${config.cor_nome_primaria || config.cor_primaria || D.primary};`,
    `--tenant-nome-secondary: ${config.cor_nome_secundaria || config.cor_secundaria || D.secondary};`,
    `--tenant-name-secondary-color: ${config.cor_nome_secundaria || config.cor_secundaria || D.secondary};`,
    `--tenant-name-secondary: ${config.cor_nome_secundaria || config.cor_secundaria || D.secondary};`,
    `--tenant-titulo-destaque: ${config.cor_titulo_destaque || config.cor_destaque || D.accent};`,
    `--tenant-title-highlight-color: ${config.cor_titulo_destaque || config.cor_destaque || D.accent};`,
    `--tenant-title-highlight: ${config.cor_titulo_destaque || config.cor_destaque || D.accent};`,
    `--tenant-footer-bg-color: ${config.cor_fundo_footer || D.footerBg};`,
    `--tenant-footer-bg: ${config.cor_fundo_footer || D.footerBg};`,
  ].join('\n  ');

  // ✅ CSS customizado específico do tenant
  // ❌ REMOVIDO: const customCSS = config.custom_css || '';
  // O custom_css estava sobrescrevendo nossas variáveis dinâmicas

  // ✅ CSS adicional para aplicar as variáveis em componentes comuns
  const componentCSS = `
    /* ✅ Aplicar cores do tenant em componentes */
    .tenant-theme {
      color: var(--tenant-text-color);
      background-color: var(--tenant-background-color);
    }
    
    .tenant-theme .btn-primary {
      background-color: var(--tenant-button-bg);
      color: white;
      border: none;
      transition: all 0.3s ease;
    }
    
    .tenant-theme .btn-primary:hover {
      background-color: var(--tenant-button-hover);
      transform: translateY(-2px);
      box-shadow: 0 4px 12px var(--tenant-shadow-color);
    }
    
    .tenant-theme .card {
      border-color: var(--tenant-border-color);
      box-shadow: 0 2px 8px var(--tenant-shadow-color);
    }
    
    .tenant-theme .header {
      background: linear-gradient(to right, var(--tenant-primary-color), var(--tenant-secondary-color));
      color: white;
    }
    
    .tenant-theme .accent-text {
      color: var(--tenant-highlight-color);
      font-weight: 600;
    }
    
    .tenant-theme .link {
      color: var(--tenant-link-color);
      text-decoration: none;
    }
    
    .tenant-theme .link:hover {
      color: var(--tenant-button-hover);
      text-decoration: underline;
    }
  `;

  return `
:root {
  ${cssVars}
}

${componentCSS}
  `.trim();
}

/**
 * Verifica se o tenant tem uma feature específica habilitada
 * ✅ FUNÇÃO QUE PODE EXECUTAR NO CLIENTE
 */
export function hasFeature(tenant: any, feature: string): boolean {
  return tenant.features?.includes(feature) || false;
}

/**
 * Obtém configuração específica do tenant
 * ✅ FUNÇÃO QUE PODE EXECUTAR NO CLIENTE
 */
export function getTenantSetting(tenant: any, key: string, defaultValue?: any): any {
  return tenant.settings?.[key] ?? defaultValue;
}

/**
 * Hook para usar tenant em componentes React
 * ✅ FUNÇÃO QUE PODE EXECUTAR NO CLIENTE
 */
export function useTenant() {
  // Implementação futura para React
  return {
    tenant: null,
    isLoading: false,
    error: null
  };
} 